import { Check } from "lucide-react";
import { useMemo } from "react";

import {
  type AgendaItem,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";

import { AgendaItemEntity } from "@src/features/agenda";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { CollapsiblePanel } from "@src/shared/components/v2/CollapsiblePanel";
import { cn } from "@src/shared/lib/utils";

export interface CompletedVisitAgendaPanelProps {
  encounterId?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface CompletedVisitAgendaPanelBodyProps {
  items: AgendaItem[];
  isLoading?: boolean;
  isError?: boolean;
}

function useCompletedVisitAgendaItems({
  encounterId,
}: {
  encounterId?: number;
}) {
  const agendaItemsQuery = useListAgendaItems(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
      refetchInterval: encounterId != null ? 5000 : false,
    },
  });
  const items = useMemo(
    () =>
      (agendaItemsQuery.data?.data.items ?? [])
        .filter(AgendaItemEntity.isActionable)
        .sort(AgendaItemEntity.compareByTimelineDesc),
    [agendaItemsQuery.data],
  );

  return {
    items,
    isLoading: agendaItemsQuery.isLoading || agendaItemsQuery.isFetching,
    isError: agendaItemsQuery.isError,
  };
}

export function CompletedVisitAgendaPanel({
  encounterId,
  open,
  defaultOpen = false,
  onOpenChange,
}: CompletedVisitAgendaPanelProps) {
  const { items, isLoading, isError } = useCompletedVisitAgendaItems({
    encounterId,
  });
  const addressedCount = items.filter(AgendaItemEntity.isCompleted).length;

  return (
    <CollapsiblePanel
      label="Agenda"
      count={`${addressedCount}/${items.length} addressed`}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className="h-full"
      contentClassName="min-h-0 overflow-auto"
    >
      <CompletedVisitAgendaPanelBody
        items={items}
        isLoading={isLoading}
        isError={isError}
      />
    </CollapsiblePanel>
  );
}

export function CompletedVisitAgendaPanelBody({
  items,
  isLoading = false,
  isError = false,
}: CompletedVisitAgendaPanelBodyProps) {
  if (isError) {
    return (
      <ErrorMessage
        className="block px-4 py-3"
        error={true}
        fallback="Unable to load agenda."
        sizing="xs"
      />
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <p className="px-4 py-3 text-[13px] text-muted-foreground">
        Loading agenda...
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="px-4 py-3 text-[13px] text-muted-foreground">
        No agenda items were captured for this visit.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-border" aria-label="Completed visit agenda">
      {items.map((item) => {
        const isCompleted = AgendaItemEntity.isCompleted(item);

        return (
          <li
            key={item.id}
            className="flex min-h-11 items-center gap-3 px-3 py-2"
            aria-label={
              AgendaItemEntity.listItemLabel(item) ?? item.description
            }
          >
            <span
              className={cn(
                "inline-flex size-[17px] shrink-0 items-center justify-center rounded-full border border-muted-foreground/40",
                isCompleted &&
                  "border-success bg-success text-success-foreground",
              )}
              aria-hidden="true"
            >
              {isCompleted ? <Check className="size-3" /> : null}
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 text-[13px] leading-5 text-foreground",
                isCompleted && "text-muted-foreground",
              )}
            >
              {item.description}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
